import {Truth} from "../common/common-types";

export enum MemoReadStatus {
  Unread = 0,
  Declined = 1,
  Agreed = 2,
}

/**
 * @property {number[]} memos - Array of memo ids
 * @property {number[]} students - Array of student (user) ids
 * @property {number[]} studentGroups - Array of student group ids
 * @property {number[]} memoCategories - Array of memo category ids (Sends an entire category of memos)
 */
export interface MemoResendData {
  memos: number[];
  students: number[];
  studentGroups: number[];
  memoCategories: number[];
}

/**
 * Data to send to accept/decline a memo
 *
 * @property {number} memoId - Memo id that we are accepting/declining
 * @property {boolean} accepted - true if student accepts memo, false otherwise
 * @property {string} declineMessage - The user inputted message if the user decline the memo
 */
export interface MemoAcceptData {
  memoId: number;
  accepted: boolean;
  declineMessage?: string;
}

export interface MemoUserLinkData {
  linkID: number;
  memoID: number;
  userID: number;
  dateSent: string;
  readStatus: MemoReadStatus;
  archiveStatus: number;
  declineMessage: string;
  dateRead: string;
}

export interface MemoReferenceLink {
  referenceID: number;
  memoID: number;
  path: string;
  name: string;
}

export interface MemoCategory {
  categoryID: number;
  category: string;
  networkID: number;
  catShort: string;
}

export interface MemoPriority {
  priorityID: number;
  priority: string;
  networkID: number;
}

export interface Memo {
  dateCreated: string;
  dateSent: string;
  actionRequired: string;
  address: string;
  name: string;
  sent: Truth | boolean;
  groupIDs: string; // Comma delimited list of groupID's
  memoID: number;
  revisionNumber: number;
  numAlpha: string;
  categoryID: number;
  num: number;
  declineOn: Truth | boolean;
  declined_recip_custom1_address: string;
  declined_recip_custom2_address: string;
  declined_recip_custom3_address: string;
  declined_recip_manager: Truth | boolean;
  declined_recip_sender: Truth | boolean;
  display_time_seconds: number;
  email: string;
  confirmText: string;
  declineText: string;
  archive: Truth | boolean;
  fromName: string;
  id: number;
  logo: string;
  memoText: string;
  networkID: string;
  priorityID: number;
  private: Truth | boolean;
  send_declined_email: Truth | boolean;
  showMemoTitle: Truth | boolean;
  subject: string;
  userID: number;
  category?: string;
  verificationQuestions: string;
}

export interface MemoTypeData extends StudentMemoTypeData {
  Memo: Memo;
  MemoGroups: Array<memoGroupsType>;
  MemoCategory: MemoCategory;
  MemoPriority: MemoPriority;
  MemoReferenceLink: MemoReferenceLink[];
  MemoStudentGroup: Array<any>;
  MemoRecipients: Array<any>;
  MemoInfo: memoInfo;
  MemoUserLink: MemoUserLinkData[];
  send: number;
}

export interface StudentMemoTypeData {
  Memo: Memo;
  MemoCategory: MemoCategory;
  MemoPriority: MemoPriority;
  MemoReferenceLink: MemoReferenceLink[];
  MemoStudentGroup: Array<any>;
  MemoUserLink: MemoUserLinkData[];
}

export interface PersonalMemoData {
  Memo: Memo;
  MemoUserLink: MemoUserLinkData;
}

export class memoType implements MemoTypeData {
  Memo: Memo;
  MemoGroups: Array<memoGroupsType>;
  MemoCategory: MemoCategory;
  MemoPriority: MemoPriority;
  MemoReferenceLink: MemoReferenceLink[];
  MemoStudentGroup: Array<any>;
  MemoRecipients: Array<any>;
  MemoInfo: memoInfo;
  MemoUserLink: MemoUserLinkData[];
  send : number

  constructor(memoData: Partial<MemoTypeData>) {
    Object.assign(this, memoData);
  }

  // Special case, needs to be JSON.stringify() into Memo.verificationQuestions on save, and unwrapped when memoType constructed
  private _memoVerificationQuestions: MemoVerificationQuestion[];

  get memoVerificationQuestions(): MemoVerificationQuestion[] {
    if (!this._memoVerificationQuestions || this._memoVerificationQuestions.length == 0) {
      if (this.Memo.verificationQuestions && this.Memo.verificationQuestions.length) this._memoVerificationQuestions = JSON.parse(this.Memo.verificationQuestions);
      else this._memoVerificationQuestions = [];
    }

    return this._memoVerificationQuestions;
  };

  set memoVerificationQuestions(verificationQuestions: MemoVerificationQuestion[]) {
    this._memoVerificationQuestions = verificationQuestions;
    this.wrapVerificationQuestions(); // You can decide if you want to auto wrap this when verification questions get set or not
  }

  wrapVerificationQuestions() {
    this.Memo.verificationQuestions = JSON.stringify(this._memoVerificationQuestions);
  }

  calculateMemoInfo() {
    if (!this.MemoInfo) this.MemoInfo = {
      acceptedCount: 0,
      declineCount: 0,
      notViewedCount: 0,
      readCount: 0,
    };

    this.MemoInfo.acceptedCount = 0;
    this.MemoInfo.declineCount = 0;
    this.MemoInfo.notViewedCount = 0;


    this.MemoUserLink.forEach(memoUser => {
      switch (memoUser.readStatus) {
        case MemoReadStatus.Agreed:
          this.MemoInfo.acceptedCount += 1;
          break;
        case MemoReadStatus.Declined:
          this.MemoInfo.declineCount += 1;
          break;
        default:
          this.MemoInfo.notViewedCount += 1;
      }
    });
  }
}

export var defaultNewMemo: MemoTypeData = {
  Memo: {
    dateCreated: null,
    dateSent: '0000-00-00 00:00:00',
    actionRequired: '',
    address: '',
    name: '',
    sent: false,
    groupIDs: null,
    memoID: null,
    revisionNumber: 0,
    numAlpha: '',
    categoryID: 0,
    num: 0,
    declineOn: 0,
    declined_recip_custom1_address: '',
    declined_recip_custom2_address: '',
    declined_recip_custom3_address: '',
    declined_recip_manager: false,
    declined_recip_sender: false,
    display_time_seconds: 0,
    email: '1',
    confirmText: 'I have read and accepted this memo',
    declineText: '',
    archive: 0,
    fromName: '',
    id: null,
    logo: '',
    memoText: '',
    networkID: null,
    priorityID: 0,
    private: false,
    send_declined_email: false,
    showMemoTitle: true,
    subject: '',
    userID: null,
    category: '',
    verificationQuestions: null
  },
  MemoGroups: [],
  MemoCategory: {
    catShort: '',
    category: '',
    categoryID: null,
    networkID: null,
  },
  MemoPriority: {
    priority: '',
    priorityID: null,
    networkID: null,
  },
  MemoReferenceLink: [],
  MemoStudentGroup: [],
  MemoRecipients: [],
  MemoInfo: null,
  MemoUserLink: [],
  send: 0
}

export interface memoInfo {
  readCount: number
  acceptedCount: number
  notViewedCount: number
  declineCount: number
}

export interface memoCategoryType {
  category: string
  categoryID: number
  networkID: number
}

export interface memoGroupsType {
  groupID: number
  networkID: number
  group_name: string
  date_created: string
  date_last_updated: string
  import: boolean
  id: number
}

export interface storeType {
  network: {}
  studentMemos :Array<any>
  memos: Array<memoType>
  memo: memoType
  searchResults: Array<memoType>
  filteredResults: Array<memoType>
  filters: filterType;
  progress: progressType;
  currentInfoText: infoStepType;
  // create: createType
  confirmation: Array<filterSubType>
  groupMemosIntoCategories: Array<any>
  selectedForReSendMemos: any
}

export class MemoVerificationQuestion {
  constructor(
    public value: string,
    public correct: string,
    public wrong1: string,
    public wrong2: string,
    public error: boolean
  ) {
  }
}

export class Question {

  constructor(
    public value: string,
    public correct: string,
    public wrong1: string,
    public wrong2: string,
    public error: boolean
  ) {
  }

}

// export class createType {
//   title: {
//     value: string,
//
//   }
//   number: {
//     value: number
//   }
//   prefix: {
//     value: string
//   }
//   category: {
//     value: string,
//   }
//   subject: {
//     value: string
//   }
//   from: {
//     value: string,
//   }
//   priority: { value: number }
//   content: { value: string }
//   recipients: { value: {} }
//   customLogo: { value: string }
//   settings: Array<any>
//   address: { value: string }
//
// }

export interface typeaheadType {
  name: string;
  memoId: string;
}

export interface progressType {
  title: string;
  text: string;
}

export interface filterSubType {
  name: string,
  memoID: number,
  networkId: number,
  value?: any,
  id?: number
}

export interface filterType {
  category: categoriesFilterTypes,
  sent: sentFilterType,
  status: statusFilterType
  priorities: categoriesFilterTypes,
}

interface sentFilterType {
  type: string;
  data: Array<filterSubType>
}

interface statusFilterType {
  type: string,
  data: Array<filterSubType>
}

interface categoriesFilterTypes {
  type: string,
  data: Array<filterSubType>
}

export interface infoStepType {
  title: string,
  text: string
}

export const priorities = [
  // {name: "Urgent", networkId: 1231, id: 2},
  // {name: "Must Read", networkId: 1231, id: 1},
  // {name: "Read within 7 days", networkId: 1231, id: 2},
  // {name: "Read immediately", networkId: 1231, id: 3}

]

export const steps = {
  create: {
    title: "Create",
    text: ""
  },
  content: {
    title: "Content",
    text: ""
  },
  recipients: {
    title: "Recipients",
    text: ""
  },
}

export const infoText = {
  title: "This will show as the title at the top of the memo (unless turned off in 'Show Header' option) and this is what the Memos reports will be sorted by. Example: 'Smoking Policy Update Jan 2016'.",
  number: "You enter your prefix, which is typically something related to the type of memo (Examples: Ops, HR, Reg) and then the second field number will follow. If you leave that field alone, it will increase sequentially for each memo",
  category: "Typically notice type or department. Examples: Safety Bulletin, Company Policy, Operations Update, Urgent Notice. Click the Edit Category button to control what is in this menu. Note that there is an option for 'Abbreviation'. This Abbreviation will be inserted into the first part of the Memo Number.",
  subject: "This is the actual title shown on the Memo. Example: 'New Smoking Restrictions Effective January 1'",
  priority: "Typical Examples: Urgent, High, Medium, Low",
  "Verification Questions": "Adding question(s) is optional.  If you want to do a little more to ensure the recipient has read and understood the memo, you can add up to three simple verification questions. Think of these as a mini ‘quiz’ about the memo contents.  They must answer the question(s) correctly before they can click the button to accept and close the memo.",
  from: "System will automatically insert name of user who creates the memo. You can override this (Examples: Safety Office, General Management, Human Resources Department)",
}

export interface NewMemoCategory {
  name: string;
  shortName: string;

}

export interface NewMemoPriority {
  name: string;
}
